import { lazy, Suspense, useMemo } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { AppRoutes } from './routes';

import { ToastContainer } from 'react-toastify';
import { getMuiTheme, useThemeStore } from '@host/stores';
import { ThemeProvider } from '@emotion/react';
import { useAppBootstrap } from './lifecycle/useBootstrap';
import { useNotificationPermissionHandler } from './lifecycle/useNotificationPermissionHandler';
import { useGuestLifecycle } from './lifecycle/useGuestLifecycle';
import { usePresenceLifecycle } from './lifecycle/usePresenceLifecycle';
import { useVersionCheck } from './lifecycle/useVersionCheck';
import { useAuthStore } from '@host/stores';
import { useEntitlementsStore } from '@/shared/stores/entitlements.store';

// Lazy — DDP service, chat store, REST, and bootstrap are excluded from the main
// bundle entirely and only downloaded once RC tokens are confirmed for this tenant.
const RocketChatBootstrap = lazy(() =>
  import('./RocketChatBootstrap').then((m) => ({ default: m.RocketChatBootstrap }))
);

function App() {
  useAppBootstrap();
  useNotificationPermissionHandler();
  useGuestLifecycle();
  usePresenceLifecycle();
  useVersionCheck();

  const themeColors = useThemeStore((s) => s.themeColors);
  const muiTheme = useMemo(() => getMuiTheme(themeColors), [themeColors]);
  const rocketchatReady = useAuthStore((s) => s.rocketchatReady);
  // Subscribed via the hook rather than read through isCustomChatEnabled(), which calls
  // getState() and so never re-renders this component when the flags finish loading. That
  // made mounting a race: whichever of `rocketchatReady` and the flag fetch settled last
  // decided the outcome, because only the former triggers a re-render. If rocketchatReady
  // flipped first the gate evaluated false and nothing ever re-evaluated it, so
  // RocketChatBootstrap — and with it the in-app chat toast, the rc-chat preference load,
  // and the mute/volume sync — never mounted at all. Reproduced reliably in Firefox, where
  // a failing events socket shifts startup timing.
  const customChatEnabled = useEntitlementsStore((s) => s.hasFeature('rocketchat.custom'));

  return (
    <ThemeProvider theme={muiTheme}>
      {rocketchatReady && customChatEnabled && (
        <Suspense fallback={null}>
          <RocketChatBootstrap />
        </Suspense>
      )}
      <ToastContainer />
      <Router>
        <AppRoutes />
      </Router>
    </ThemeProvider>
  );
}

export default App;
